import {
  ResourceKind,
  resourceTitles,
} from "app/utils/request/scheduling/facilityResources";
import { SHORT_CARD_MAX_MINUTES } from "./titleWithLabel";

export interface CardLabels {
  // The bay, on the time line. The Coaches view only.
  resourceLabel?: string;
  // The coach, on a line of its own. The Resources view only (decision Q15).
  coachName?: string;
}

/**
 * The extra words on a session card or a hold card.
 *
 * The Coaches view puts the bay on the time line. The Resources view puts
 * the coach on a line under the athlete name, because the column already
 * shows the bay. With the feature off, the booking has no bay, so the
 * Coaches view gets nothing.
 */
export function cardLabels(
  extendedProps: {
    coachProfile?: { firstName?: string; lastName?: string } | null;
    scheduleResources?: { title: string; kind?: ResourceKind }[];
  },
  facilityResourceView: boolean,
): CardLabels {
  if (!facilityResourceView) {
    const bay = resourceTitles({
      scheduleResources: extendedProps.scheduleResources,
    })[0];

    return bay ? { resourceLabel: bay } : {};
  }

  const coachName = [
    extendedProps.coachProfile?.firstName,
    extendedProps.coachProfile?.lastName,
  ]
    .filter(Boolean)
    .join(" ");

  return coachName ? { coachName } : {};
}

// The shortest card, in minutes, that gives the coach a line of its own.
export const COACH_LINE_MIN_MINUTES = 60;
// The shortest card, in minutes, that wraps the title to 2 lines (R12).
export const WRAP_TITLE_MIN_MINUTES = 90;

export interface ResourceCardLines {
  // The title wraps to 2 lines. Else it keeps 1 line with an ellipsis.
  wrapTitle: boolean;
  // The athlete names get a line of their own.
  athleteLine: boolean;
  // The coach goes on the title line, after the title.
  coachOnTitleLine: boolean;
  // The coach gets a line of its own.
  coachLine: boolean;
  // The start time gets a line of its own.
  timeLine: boolean;
}

/**
 * The lines of a card in a bay column, for the height of the card (Figma
 * 166:67254, plus the coach of decision Q15).
 *
 * - 30 minutes or less: the title line only, with the coach on it.
 * - Less than 60 minutes: the title line with the coach on it, and the time.
 * - 60 minutes: the title, the coach and the time, each on 1 line.
 * - More than 60 minutes: the athlete names too, under the title.
 * - 90 minutes or more: the title wraps to 2 lines.
 *
 * Each line that is too long ends in an ellipsis. The time line never goes
 * out of the card: a card that has room for 2 lines or more shows it.
 */
export function resourceCardLines(
  coachName: string | undefined,
  durationInMinutes: number | undefined,
): ResourceCardLines {
  const minutes = durationInMinutes ?? 0;
  const hasCoach = Boolean(coachName);
  const timeLine = minutes > SHORT_CARD_MAX_MINUTES;
  const coachLine = hasCoach && minutes >= COACH_LINE_MIN_MINUTES;

  return {
    wrapTitle: minutes >= WRAP_TITLE_MIN_MINUTES,
    athleteLine: minutes > COACH_LINE_MIN_MINUTES,
    coachOnTitleLine: hasCoach && !coachLine,
    coachLine,
    timeLine,
  };
}

/**
 * The title line of a card in a bay column: the title, and the coach after it
 * when the card has no room for a coach line.
 */
export function resourceCardTitle(
  title: string,
  coachName: string | undefined,
  lines: ResourceCardLines,
): string {
  return lines.coachOnTitleLine ? `${title} \u00B7 ${coachName}` : title;
}

/**
 * Whether the time line shows the bay. A card of 30 minutes or less shows the
 * bay on the title line (see `titleWithLabel`), so the time line leaves it out.
 */
export function resourceLineShown(
  resourceLabel: string | undefined,
  durationInMinutes: number | undefined,
): boolean {
  return (
    Boolean(resourceLabel) &&
    (durationInMinutes ?? 0) > SHORT_CARD_MAX_MINUTES
  );
}
